/**
 * globo-grid-bridge.js — puente entre la grilla de la app Globo y el theme.
 *
 * La grilla de colecciones y el buscador los renderiza Globo con su propio motor
 * (snippets/globo.filter.product.liquid esta envuelto en {% raw %}: los filtros de
 * adentro NO los ejecuta Shopify).
 *
 * El boton de agregar al carrito de la card es un <form> POST plano, ajeno a los custom
 * elements del theme (<product-form> ni siquiera esta registrado en las paginas de
 * coleccion: product.js no se carga ahi). Sin esto, agregar desde la grilla hace
 * POST -> 302 -> navegacion completa a /cart, sin cart drawer.
 *
 * Idempotente y sin dependencias: app.js carga en todas las paginas. El submit va por
 * delegacion, asi que sobrevive a que Globo redibuje la grilla al filtrar u ordenar.
 *
 * NO reformatea los precios de la card, y no tiene que hacerlo.
 *
 * El separador de miles ya se corrige en la plantilla de Globo con un filtro Liquid
 * (`| money | replace: ',', '.'` en globo.filter.product.liquid). Verificado en vivo
 * el 2026-07-29 sobre /collections/all: AR renderiza "$1.092.630". En la rama `us`
 * ese replace NO va y no es un olvido: USD se escribe "$2,180.00", que es exactamente
 * lo que Globo emite sin tocar nada.
 *
 * Queda desmentida de paso la premisa vieja de que "el formato no sale de
 * shop.money_format". Si sale de ahi: el valor es "${{amount_no_decimals}}" y Shopify
 * elige el separador segun la MONEDA de la tienda, no segun el nombre del placeholder
 * -por eso la ficha de producto en ARS imprime punto con el mismo money_format-.
 *
 * Lo que SI sigue vigente como advertencia: el motor de Globo entrega el precio en
 * UNIDADES DE MONEDA, no en centavos (`{{ variant.price }}` -> 4378742, no
 * 437874200), mientras que `formatMoney` del theme espera centavos. Pasar uno al otro
 * ya rompio una vez (revertido en f7637d6): la grilla anunciaba $43.787 para un
 * producto de $4.378.742 y el carrito cobraba el precio real. Por eso el arreglo es
 * un replace de SEPARADORES sobre los digitos ya renderizados y nunca un recalculo.
 */
(() => {
  'use strict';

  const SECTIONS = [
    { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
    { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer--inner' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-count' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-amount' }
  ];

  /* -------------------------------------------------- agregar al carrito */

  const renderContents = (parsedState) => {
    const drawerHtml = parsedState.sections?.['cart-drawer'];
    if (!drawerHtml || !new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('.cart-drawer--inner')) return false;
    SECTIONS.forEach((section) => {
      const target = document.getElementById(section.id);
      if (!target) return; // en coleccion no existe #Cart (main-cart): se saltea
      const sourceHtml = parsedState.sections && parsedState.sections[section.section];
      if (!sourceHtml) return;

      const inner = new DOMParser()
        .parseFromString(sourceHtml, 'text/html')
        .querySelector(section.selector);
      if (!inner) return;

      const elementToReplace = target.querySelector(section.selector) || target;
      // innerHTML es EXCEPCION consciente a la regla del proyecto (ver docs/frontend.md):
      // el dato es HTML de la Section Rendering API de Shopify, no texto, asi que
      // textContent no aplica. Y NO cambiar por replaceChildren(...inner.childNodes):
      // innerHTML nunca ejecuta <script>, pero mover nodos de DOMParser al documento
      // vivo SI los ejecuta -- seria abrir un vector que hoy no existe.
      elementToReplace.innerHTML = inner.innerHTML;
    });

    const drawer = document.getElementById('Cart-Drawer');
    if (!drawer || typeof drawer.open !== 'function') return false;
    drawer.open();
    if (typeof drawer.removeProductEvent === 'function') drawer.removeProductEvent();
    return true;
  };

  const setLoading = (form, loading) => {
    const button = form.querySelector('.spf-product__form-btn-addtocart');
    if (!button) return;
    button.classList.toggle('loading', loading);
    if (loading) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
  };

  const onSubmit = (event) => {
    const form = event.target.closest('.spf-product__form');
    if (!form) return;

    // Si el drawer esta apagado en el editor, el POST nativo a /cart es el
    // comportamiento correcto: no hay drawer que abrir.
    const drawer = document.getElementById('Cart-Drawer');
    if (!drawer || typeof drawer.open !== 'function' || !window.CollinoCart) return;
    if (window.theme && window.theme.settings && window.theme.settings.cart_drawer === false) return;
    if (typeof theme === 'undefined' || !theme.routes || !theme.routes.cart_add_url) return;

    event.preventDefault();
    if (form.dataset.submitting === 'true') return; // dos toques rapidos = dos unidades
    form.dataset.submitting = 'true';
    setLoading(form, true);

    CollinoCart.clearError(form);
    const button = form.querySelector('.spf-product__form-btn-addtocart');
    const formData = new FormData(form);
    formData.append('sections', SECTIONS.map((section) => section.section).join(','));
    formData.append('sections_url', window.location.pathname);
    let added = false;
    return CollinoCart.enqueue(async () => {
      const response = await fetch(theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: formData
      });
      const state = await response.json();
      if (!response.ok || state.status || state.errors) throw new Error(CollinoCart.message(state, "No pudimos confirmar el agregado. Revisá tu carrito antes de reintentar."));
      added = true;
      if (!renderContents(state)) window.location.assign(CollinoCart.cartUrl());
      if (typeof dispatchCustomEvent === 'function') {
        dispatchCustomEvent('cart:item-added', { product: state.items ? state.items[0] : state });
      }
    }).catch((error) => {
      // Despues del primer POST, el fallback solo puede leer o navegar al carrito.
      // Una respuesta ilegible puede corresponder a una escritura ya aceptada.
      if (added) window.location.assign(CollinoCart.cartUrl());
      else CollinoCart.showError(form, button, error instanceof SyntaxError || error instanceof TypeError ? "No pudimos confirmar el agregado. Revisá tu carrito antes de reintentar." : error.message);
    }).finally(() => {
      form.dataset.submitting = 'false';
      setLoading(form, false);
    });
  };

  /* ------------------------------------- Escape cierra el panel de filtros */

  /**
   * El panel de filtros de Globo no cierra con Escape (verificado en el preview el
   * 2026-07-29): el cart drawer del theme si, asi que el sitio se comportaba de dos
   * maneras distintas ante la misma tecla. Quien abre el panel con teclado quedaba
   * sin salida obvia.
   *
   * No se toca el JS de la app: se le da un empujon a su propio boton de cierre.
   * Todo esta escrito para degradar sin ruido — si Globo renombra la clase, el
   * selector no encuentra nada y Escape simplemente no hace nada, como antes.
   */
  const onEscape = (event) => {
    if (event.key !== 'Escape') return;

    // Globo marca el panel abierto con `offcanvas-open` en <html>. Es la unica senal
    // fiable: el boton de cierre esta REPETIDO en el DOM y medir el primero daba
    // falsos negativos (reportaba cerrado con el panel abierto en pantalla).
    const root = document.documentElement;
    if (!root.classList.contains('offcanvas-open')) return;

    // Con el cart drawer abierto el Escape es suyo: cerrarlo es su responsabilidad
    // y no queremos cerrar dos cosas de un saque. Cuando esta cerrado lleva `inert`.
    const cartDrawer = document.getElementById('Cart-Drawer');
    if (cartDrawer && !cartDrawer.hasAttribute('inert')) return;

    // Primero los botones de cierre de la propia app, para que corra su limpieza.
    [...document.querySelectorAll('.gf-close-canvas')]
      .filter((boton) => boton.getBoundingClientRect().width > 0)
      .forEach((boton) => boton.click());

    // Y si la app no reacciono, se saca la clase de estado: es lo que efectivamente
    // cierra el panel. Verificado en el preview de AR el 2026-07-29.
    if (root.classList.contains('offcanvas-open')) root.classList.remove('offcanvas-open');

    // Devolver el foco al disparador, igual que hace el cart drawer. Sin esto el foco
    // queda en un panel que ya no existe y el siguiente Tab arranca desde el principio.
    const toggle = document.querySelector('.gf-refine-toggle-mobile button, .gf-refine-toggle-mobile [role="button"], .gf-refine-toggle-mobile');
    if (toggle && typeof toggle.focus === 'function') toggle.focus();
  };

  /* ------------------------------------- limpiar busqueda interna Globo */

  const getUrlWithoutGloboSearch = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('q');
    url.searchParams.delete('options[prefix]');
    url.searchParams.delete('options%5Bprefix%5D');
    return `${url.pathname}${url.search}${url.hash}`;
  };

  const syncSearchClear = () => {
    const grid = document.getElementById('gf-products');
    if (!grid) return;

    const existing = document.querySelector('[data-globo-search-clear]');
    const params = new URLSearchParams(window.location.search);
    const hasSearch = params.get('q');
    const productCount = grid.querySelectorAll('.spf-product-card-wrapper').length;
    const shouldShow = Boolean(hasSearch) && productCount === 0;

    if (!shouldShow) {
      if (existing) existing.remove();
      return;
    }

    if (existing) {
      existing.setAttribute('href', getUrlWithoutGloboSearch());
      return;
    }

    const emptyMessage = grid.querySelector('.empty, .gf-empty, [class*="empty"], [class*="no-result"]') || grid;
    const clearLink = document.createElement('a');
    clearLink.href = getUrlWithoutGloboSearch();
    clearLink.className = 'globo-search-clear';
    clearLink.dataset.globoSearchClear = 'true';
    clearLink.textContent = 'Limpiar búsqueda';
    emptyMessage.insertAdjacentElement('afterend', clearLink);
  };

  // Globo restores results on history navigation but leaves the sort label stale.
  // Read its configured default (not the first option) and reuse its translated labels.
  // This only reflects state: never click an option or request another product grid.
  const syncSortControl = () => {
    const button = document.querySelector('#sortButton');
    // The app replaces the initial span with a text node after a user selection.
    const label = button?.querySelector('span') || button;
    const options = document.querySelectorAll('#globo-dropdown-sort_options [data-sort]');
    const sort = new URLSearchParams(window.location.search).get('sort_by')
      || window.GloboEmbedFilterConfig?.collection?.sort;
    const selected = [...options].find((option) => option.dataset.sort === sort);
    if (!label || !selected) return;
    const text = selected.textContent.trim();
    if (label.textContent !== text) label.textContent = text;
    options.forEach((option) => {
      option.setAttribute('aria-selected', String(option === selected));
      option.tabIndex = option === selected ? 0 : -1;
    });
  };

  const syncGridControls = () => {
    syncSearchClear();
    syncSortControl();
  };

  let controlsFrame = 0;
  // Only history needs repair. Observing every grid redraw races the app's own
  // option click, which changes the label before updating the URL.
  const scheduleGridControls = () => {
    if (controlsFrame) return;
    controlsFrame = window.requestAnimationFrame(() => {
      controlsFrame = 0;
      syncGridControls();
    });
  };

  // Values are formatted by Globo in its own currency units, never by theme.formatMoney.
  const onVariantChange = (event) => {
    const select = event.target.closest?.('.spf-product__variants');
    const card = select?.closest('.spf-product-card');
    const option = select?.selectedOptions?.[0];
    if (!card || !option || option.dataset.cardPrice === undefined) return;
    const data = option.dataset;
    const consult = data.cardConsult === 'true';
    const available = data.available === 'true';
    const hasTransfer = !consult && data.cardHasTransfer === 'true';
    const hasDiscount = !consult && data.cardHasDiscount === 'true';
    const setText = (selector, value) => {
      const node = card.querySelector(selector);
      if (node) node.textContent = value;
    };
    const setHidden = (selector, hidden) => {
      const node = card.querySelector(selector);
      if (node) node.hidden = hidden;
    };
    setHidden('[data-globo-consult]', !consult);
    setHidden('[data-globo-price]', consult);
    const consultLink = card.querySelector('.spf-product-card__consultar-btn');
    if (consultLink && data.cardConsultUrl) consultLink.href = data.cardConsultUrl;
    setText('.spf-product-card__price', data.cardPrice);
    setText('.spf-product-card__oldprice', data.cardCompare);
    setText('.spf-transfer-price', data.cardTransfer);
    setText('[data-globo-discount]', data.cardDiscount + '% OFF');
    setHidden('[data-globo-discount]', !hasDiscount);
    setHidden('[data-globo-soldout]', available);
    setHidden('.spf-price-transfer', !hasTransfer);
    const oldPrice = card.querySelector('.spf-product-card__oldprice');
    if (oldPrice) oldPrice.style.display = hasDiscount ? '' : 'none';
    card.querySelector('.spf-product-card__price-wrapper')?.classList.toggle('spf-product-card__price-wrapper--has-transfer', hasTransfer);
    card.classList.toggle('spf-soldout', !available);
    const wrapper = card.closest('.spf-product-card-wrapper');
    if (wrapper) wrapper.dataset.productoSinPrecio = String(consult);
    const form = card.querySelector('.spf-product__form');
    form?.classList.toggle('spf-product__form--hidden', consult);
    select.disabled = consult || !available;
    const addButton = card.querySelector('.spf-product__form-btn-addtocart');
    if (addButton) {
      addButton.disabled = consult || !available;
      const label = addButton.querySelector('span');
      if (label) label.textContent = available ? addButton.dataset.available : addButton.dataset.soldout;
    }
    const shippingMessage = !consult && available ? (data.cardShippingMessage || '') : '';
    const shipping = shippingMessage !== '';
    setText('[data-globo-shipping-label]', shippingMessage);
    const shippingLink = card.querySelector('[data-globo-shipping] a');
    if (shippingLink) shippingLink.dataset.shippingFree = String(shipping && data.cardShipping === 'true');
    setHidden('[data-globo-shipping] a', !shipping);
    setHidden('[data-globo-shipping] .spf-product-card__shipping--placeholder', shipping || consult);
    card.querySelectorAll('.spf-product-card__title a, a.spf-product-card__image-wrapper, .spf-product__form-btn-options').forEach((link) => {
      const url = new URL(link.href, window.location.href);
      url.searchParams.set('variant', option.value);
      link.href = url.pathname + url.search + url.hash;
    });
  };

  /* ------------------------------------------------------------------ init */

  const init = () => {
    if (document.body.dataset.globoBridgeInit === 'true') return;
    document.body.dataset.globoBridgeInit = 'true';

    // Delegacion: sobrevive a que Globo redibuje la grilla al filtrar u ordenar.
    document.addEventListener('submit', onSubmit);
    document.addEventListener('change', onVariantChange);
    document.addEventListener('keydown', onEscape);
    syncGridControls();
    const container = document.getElementById('ProductGridContainer');
    if (container) {
      new MutationObserver(syncSearchClear).observe(container, { childList: true, subtree: true });
    }
    window.addEventListener('popstate', scheduleGridControls);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
